// keep this file isomorphic
import moment from 'moment';

import {ScrollingCalendarDateSelection} from 'common/components/scrolling-calendar/util';
import {FEATURE_GATES_UNLIMITED} from 'common/report/constants';
import {formatDate, parseDate} from 'common/util/date';
import {pluralize} from 'common/util/string';
import {CALENDAR_DATE_FORMAT, PresetOption} from 'common/widgets/date-range-picker/models/date-range';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {END_OF_TODAY, START_OF_TODAY} from 'common/widgets/date-range-picker/models/presets';
import {DateRangeType, RelativeDate, TimeUnit} from 'common/widgets/date-range-picker/models/types';

// no need to define a Typescript enum for this, these are strings meant only to be read by humans
export const TRACKED_USE_CASES = [`query-builder`, `inline-cohort`, `filters`];

// DatePickerMode is not supposed to map 1-1 with DateRangeType, it is purely for UI presentation
export enum DatePickerMode {
  InTheLast = `in the last`,
  Between = `between`,
  Since = `since`,
  On = `on`,
}

export function getDatePickerMode(dateRange: DateRange): DatePickerMode {
  if (dateRange.isRolling()) {
    return DatePickerMode.InTheLast;
  }

  switch (dateRange.type) {
    case DateRangeType.RelativeAfter: {
      return DatePickerMode.InTheLast;
    }
    case DateRangeType.On: {
      return DatePickerMode.On;
    }
    case DateRangeType.Between: {
      return DatePickerMode.Between;
    }
    case DateRangeType.Since: {
      return DatePickerMode.Since;
    }

    default:
      throw new Error(`"date-range" attribute contains DateRange info with unsupported DateRangeType`);
  }
}

export function getCalendarSelection(dateRange: DateRange, asOfDate: moment.Moment): ScrollingCalendarDateSelection {
  const {from, to} = dateRange.getCalendarDatesAsOf(asOfDate.format(CALENDAR_DATE_FORMAT));
  const startDate = moment(parseDate(from));
  let endDate = moment(parseDate(to));

  // clamp endDate to startDate
  if (startDate.isSameOrAfter(endDate)) {
    endDate = moment(parseDate(from));
  }

  if (startDate.isSameOrBefore(endDate, `day`)) {
    const showOffset = dateRange.isRolling() && endDate.isBefore(asOfDate, `day`);

    return {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      highlightStart: true,
      highlightEnd: !endDate.isSame(startDate, `day`) && getDatePickerMode(dateRange) === DatePickerMode.Between,
      ...(showOffset ? {offsetFrom: asOfDate.toISOString()} : {}),
    };
  }

  return null;
}

export function getRelativeWindowUnit(dateRange: DateRange): TimeUnit {
  if (dateRange.window) {
    return dateRange.window.unit;
  }
  if (dateRange.isRolling()) {
    const relativeTo = dateRange.to as RelativeDate;
    if (relativeTo.unit) {
      return relativeTo.unit;
    }
  }
  return TimeUnit.Day;
}

export function getRelativeWindowValue(dateRange: DateRange, asOfDate: moment.Moment): number {
  if (dateRange.window) {
    return dateRange.window.value;
  }

  const {from, to} = dateRange.getCalendarDatesAsOf(asOfDate.format(`YYYY-MM-DD`));
  const startDate = moment(parseDate(from));
  const endDate = moment(parseDate(to));
  const windowUnit = getRelativeWindowUnit(dateRange);
  return endDate.diff(startDate, windowUnit);
}

// returns offset if there is a valid offset (note: offsets with non-Day units are currently not considered valid)
export function getRelativeOffset(dateRange: DateRange, asOfDate: moment.Moment): null | RelativeDate {
  if (dateRange.window?.unit === TimeUnit.Day) {
    return {unit: TimeUnit.Day, value: 0};
  }
  if (dateRange.isRolling()) {
    if (typeof dateRange.to === `object`) {
      const relativeTo = dateRange.to;
      if (relativeTo.unit === TimeUnit.Day) {
        return relativeTo;
      }
    } else if (typeof dateRange.to === `string`) {
      const {to} = dateRange.getCalendarDatesAsOf(asOfDate.format(CALENDAR_DATE_FORMAT));
      const endDate = moment(parseDate(to));
      const value = asOfDate.diff(endDate, TimeUnit.Day);
      return {unit: TimeUnit.Day, value};
    }
  }
  return null;
}

// ------- note: always output DateRange attributes instead of DateRange class instances because the appropriate class is determined by display context, which is a UI concern -------

export function getInTheLastDateRangeAttributes(window: RelativeDate, offset: null | RelativeDate): Partial<DateRange> {
  if (offset) {
    if (window.unit !== offset.unit) {
      throw new Error(`window and offset units must match`);
    }
    if (offset.unit !== TimeUnit.Day) {
      throw new Error(`offset unit must be Day`);
    }

    if (offset.value > 0) {
      const isSingleDay = window.value === 0 && window.unit === TimeUnit.Day;
      return {
        type: isSingleDay ? DateRangeType.On : DateRangeType.Between,
        to: offset,
        from: {
          unit: window.unit,
          value: offset.value + window.value,
        },
        rolling: true,
      };
    }
  }

  if (window.value === 0) {
    return {
      from: START_OF_TODAY,
      to: END_OF_TODAY,
      type: DateRangeType.Between,
    };
  }

  return {
    type: DateRangeType.RelativeAfter,
    window,
  };
}

export function getBetweenDateRangeAttributes(fromDate: moment.Moment, toDate: moment.Moment): Partial<DateRange> {
  const firstDate = fromDate.isSameOrBefore(toDate, `day`) ? fromDate : toDate;
  const secondDate = fromDate.isSameOrAfter(toDate, `day`) ? fromDate : toDate;
  return {
    type: DateRangeType.Between,
    to: secondDate.format(CALENDAR_DATE_FORMAT),
    from: firstDate.format(CALENDAR_DATE_FORMAT),
  };
}

export function getOnDateRangeAttributes(onDate: moment.Moment): Partial<DateRange> {
  return {
    type: DateRangeType.On,
    from: onDate.format(CALENDAR_DATE_FORMAT),
    to: onDate.format(CALENDAR_DATE_FORMAT),
  };
}

export function getSinceDateRangeAttributes(sinceDate: moment.Moment): Partial<DateRange> {
  return {
    type: DateRangeType.Since,
    from: sinceDate.format(CALENDAR_DATE_FORMAT),
  };
}

export function getSimilarDateRange(
  dateRange: DateRange,
  asOfDate: moment.Moment,
  type: DateRangeType,
): null | Partial<DateRange> {
  const {from, to} = dateRange.getCalendarDatesAsOf(asOfDate.format(CALENDAR_DATE_FORMAT));
  const fromDate = moment(parseDate(from));
  const toDate = moment(parseDate(to));

  switch (type) {
    case DateRangeType.RelativeBefore:
    case DateRangeType.RelativeAfter:
    case DateRangeType.NotRelativeAfter: {
      const windowValue = getRelativeWindowValue(dateRange, asOfDate);
      const windowUnit = getRelativeWindowUnit(dateRange);
      const offsetDays = asOfDate.diff(toDate, TimeUnit.Day);
      const canBeRolling = dateRange.canUseRolling() && windowUnit === TimeUnit.Day && offsetDays > 0;

      if (canBeRolling) {
        const isSingleDay = windowValue === 0 && windowUnit === TimeUnit.Day;
        return {
          type: isSingleDay ? DateRangeType.On : DateRangeType.Between,
          to: {
            unit: windowUnit,
            value: offsetDays,
          },
          from: {
            unit: windowUnit,
            value: offsetDays + windowValue,
          },
          rolling: true,
        };
      } else if (windowValue === 0) {
        return {
          from: START_OF_TODAY,
          to: END_OF_TODAY,
          type: DateRangeType.Between,
        };
      } else {
        return {
          type,
          window: {unit: windowUnit, value: windowValue},
        };
      }
      break;
    }
    case DateRangeType.Between:
    case DateRangeType.NotBetween: {
      return {
        ...getBetweenDateRangeAttributes(fromDate, toDate),
        type,
      };
    }
    case DateRangeType.On:
    case DateRangeType.NotOn: {
      return {
        from: getBetweenDateRangeAttributes(fromDate, toDate).from,
        to: getBetweenDateRangeAttributes(fromDate, toDate).from,
        type,
      };
    }
    case DateRangeType.Since: {
      return getSinceDateRangeAttributes(fromDate);
    }
    case DateRangeType.Before: {
      return {
        type,
        to: getSinceDateRangeAttributes(fromDate).from,
      };
    }
  }
  return null;
}

export function getSimilarDateRangeForPickerMode(
  dateRange: DateRange,
  asOfDate: moment.Moment,
  pickerMode: DatePickerMode,
): null | Partial<DateRange> {
  const defaultDateRangeType: DateRangeType = (() => {
    switch (pickerMode) {
      case DatePickerMode.InTheLast:
        return DateRangeType.RelativeAfter;
      case DatePickerMode.On:
        return DateRangeType.On;
      case DatePickerMode.Between:
        return DateRangeType.Between;
      case DatePickerMode.Since:
        return DateRangeType.Since;
      default: {
        const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
        throw new Error(`unknown picker mode ${unreachable}`);
      }
    }
  })();
  return getSimilarDateRange(dateRange, asOfDate, defaultDateRangeType);
}

export function isMaxHistoryFinite(maxHistoryDays: number): boolean {
  if (!Number.isInteger(maxHistoryDays)) {
    // note: Number.isInteger() also checks that the value is finite
    return false;
  }
  if (maxHistoryDays <= 0) {
    return false;
  }
  if (maxHistoryDays === FEATURE_GATES_UNLIMITED) {
    return false;
  }
  return true;
}

export function generateDateRangeTrackingProps(
  dateRange: DateRange,
  reportName: string,
  pickerMode: DatePickerMode,
  projectAsOfDate: moment.Moment,
  trackedUseCase: string,
): any {
  if (!dateRange) {
    return {
      'Report name': reportName,
      'Date picker mode': pickerMode,
      'Date range from': null,
      'Date range to': null,
      'Date window duration (in days)': null,
      'Date window units selection': null,
      Source: trackedUseCase,
    };
  }

  const {from, to} = dateRange.getCalendarDatesAsOf(projectAsOfDate.format(CALENDAR_DATE_FORMAT));
  const fromDate = moment(parseDate(from));
  const toDate = moment(parseDate(to));
  const dateRangePickerMode = getDatePickerMode(dateRange);
  return {
    'Report name': reportName,
    'Date picker mode': getDatePickerMode(dateRange),
    'Date range from': fromDate.toISOString(),
    'Date range to': toDate.toISOString(),
    'Date window duration (in days)': toDate.diff(fromDate, `day`),
    'Date window units selection':
      dateRangePickerMode === DatePickerMode.InTheLast ? getRelativeWindowUnit(dateRange) : null,
    Source: trackedUseCase,
  };
}

export function getDateRangeExternalSelectorPrefix(
  dateRange: null | DateRange,
  matchingPreset: null | PresetOption,
): string {
  if (!dateRange) {
    return ``;
  }

  if (matchingPreset) {
    return `in the`;
  }

  const pickerMode = getDatePickerMode(dateRange);
  switch (pickerMode) {
    case DatePickerMode.Between:
    case DatePickerMode.On:
      return `from`;
    case DatePickerMode.Since:
      return `since`;
    case DatePickerMode.InTheLast:
      return `in the`;
    default: {
      const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
      throw new Error(`unknown picker mode ${unreachable}`);
    }
  }
}

export function getDateRangeLabel(
  dateRange: null | DateRange,
  matchingPreset: null | PresetOption,
  projectAsOfDate: null | string,
  now: moment.Moment,
  useExternalSelectorPrefix = false,
): string {
  if (!dateRange) {
    return `Select Date`;
  }
  const parseAndFormatDate = (val) => formatDate(parseDate(val));

  const explicitAsOfDate = parseDate(projectAsOfDate);
  const asOfDate = explicitAsOfDate ? moment(explicitAsOfDate) : now;

  let rangeLabel = `unknown`;
  let asOfLabel: string = explicitAsOfDate ? ` (as of ${formatDate(explicitAsOfDate)})` : ``;

  const pickerMode = getDatePickerMode(dateRange);
  switch (pickerMode) {
    case DatePickerMode.On:
    case DatePickerMode.Between: {
      const {from, to} = dateRange.getCalendarDatesAsOf(projectAsOfDate);
      rangeLabel = `${parseAndFormatDate(from)} — ${parseAndFormatDate(to)}`;
      asOfLabel = ``;
      break;
    }
    case DatePickerMode.Since: {
      const {from} = dateRange.getCalendarDatesAsOf(projectAsOfDate);
      const internalPrefix = useExternalSelectorPrefix ? `` : `Since `;
      rangeLabel = `${internalPrefix}${parseAndFormatDate(from)}`;
      if (explicitAsOfDate) {
        asOfLabel = ` (until ${formatDate(explicitAsOfDate)})`;
      }
      break;
    }
    case DatePickerMode.InTheLast: {
      const windowValue = getRelativeWindowValue(dateRange, asOfDate);
      const windowUnit = getRelativeWindowUnit(dateRange);
      const offset = getRelativeOffset(dateRange, asOfDate);

      rangeLabel = `Last ${windowValue} ${pluralize(windowUnit, windowValue)}`;
      if (offset?.value > 0 && !matchingPreset) {
        rangeLabel = `${rangeLabel} ending ${offset.value} ${pluralize(offset.unit, offset.value)} ago`;
      }
      break;
    }
    default: {
      const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
      throw new Error(`unknown picker mode ${unreachable}`);
    }
  }

  return `${matchingPreset?.label || rangeLabel}${asOfLabel}`;
}
